import { getTokenHeader } from "@/helpers/commonHelper";
import axios from "axios";
const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
import { confirmWarningConfig } from "@/configuration/confirmConfigurations";
import EditFeature from "@/components/POI/EditFeature/EditFeature.vue";
import "./GlobalFeatureConfig.scss";
import { PoiSteps } from "@/helpers/poiHelper";

export default {
  name: "GlobalFeatureConfig",
  props: [
    "featureGroups",
    "selectedFeatureGroups",
    "width",
    "buttonText",
    "global",
    "isPoiAdmin"
  ],
  data: () => ({
    createIsValid: false,
    isValid: false,
    selectedGroup: {},
    showCreateInput: false,
    createInputValue: "",
    createInputGroups: [],
    createInputTypes: [],
    createInputValid: false,
    poiSteps: PoiSteps,
    searchTerm: "",
    openGroups: {},
    showEditDialog: false,
    editDialogType: "feature",
    dialogFeature: null,
    featureDialogMode: "edit",
    dialogLoading: false
  }),
  async mounted() {
    await this.$store.dispatch("getCategoryGroups");
    await this.$store.dispatch("getCategoryTypes", true);
  },
  methods: {
    openGroup(id) {
      this.$set(this.openGroups, id, true);
    },
    toggleGroup(id) {
      this.$set(this.openGroups, id, !this.openGroups[id]);
    },
    isGroupOpen(id) {
      return !!this.openGroups[id];
    },
    groupMatchesSearch(group) {
      if (!this.hasActiveSearch) return true;
      const groupTitle = (group?.title || "").toLowerCase();
      return groupTitle.includes(this.normalizedSearchTerm);
    },
    featureMatchesSearch(feature) {
      if (!this.hasActiveSearch) return true;
      const haystack = [
        feature?.title,
        feature?.shortTitle,
        feature?.mappingField
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(this.normalizedSearchTerm);
    },
    hasMatchingFeature(group) {
      return (group?.features || []).some(feature =>
        this.featureMatchesSearch(feature)
      );
    },
    getVisibleFeatures(group) {
      const features = group?.features || [];
      if (!this.hasActiveSearch || this.groupMatchesSearch(group))
        return features;
      return features.filter(feature => this.featureMatchesSearch(feature));
    },
    shouldRenderGroupContent(group) {
      return this.isGroupOpen(group._id) || this.hasActiveSearch;
    },
    openGroupDialog(group) {
      this.selectedGroup = {
        ...group,
        services: [...(group.services || [])],
        categoryTypes: [...(group.categoryTypes || [])],
        categoryGroups: [...(group.categoryGroups || [])]
      };
      this.editDialogType = "group";
      this.showEditDialog = true;
      this.openGroup(group._id);
    },
    createEmptyFeature(groupId) {
      return {
        title: "",
        shortTitle: "",
        pictureURL: "",
        mappingField: "",
        isHighlight: false,
        visible_to: "all",
        editable_by: "all",
        internalControlFeature: false,
        internal_control_feature: false,
        featureGroup: groupId
      };
    },
    openCreateFeatureDialog(groupId) {
      this.openGroup(groupId);
      this.dialogFeature = this.createEmptyFeature(groupId);
      this.editDialogType = "feature";
      this.featureDialogMode = "create";
      this.showEditDialog = true;
    },
    openFeatureDialog(feature, groupId) {
      this.openGroup(groupId);
      this.dialogFeature = {
        ...feature,
        featureGroup: feature?.featureGroup || groupId
      };
      this.editDialogType = "feature";
      this.featureDialogMode = "edit";
      this.showEditDialog = true;
    },
    closeEditDialog(force = false) {
      if (this.dialogLoading && !force) return;
      this.showEditDialog = false;
      this.dialogFeature = null;
      this.selectedGroup = {};
      this.editDialogType = "feature";
    },
    async saveDialogFeature(feature) {
      if (this.dialogLoading) return;
      this.dialogLoading = true;
      const method = this.featureDialogMode === "create" ? "post" : "put";
      try {
        const isSaved = await this.saveFeature(feature, method);
        if (isSaved) this.closeEditDialog(true);
      } finally {
        this.dialogLoading = false;
      }
    },
    async saveDialogGroup() {
      if (this.dialogLoading) return;
      this.dialogLoading = true;
      const payload = {
        group: this.selectedGroup,
        successText: "Erfolgreich gespeichert!"
      };

      try {
        const onUpdateGroup = this.$listeners?.updateGroup;
        if (typeof onUpdateGroup === "function") await onUpdateGroup(payload);
        else this.$emit("updateGroup", payload);

        this.closeEditDialog(true);
      } finally {
        this.dialogLoading = false;
      }
    },
    async saveFeature(feature, method = "put") {
      const headers = getTokenHeader();
      try {
        const res = await axios[method](
          `${env.poi_api_client_url}feature/${
            method === "put" ? feature._id : ""
          }`,
          feature,
          { headers }
        );
        if (res.status === 200 || res.status === 201) {
          this.$emit("success", "Erfolgreich gespeichert!");

          // No need to wait for this dispatch here
          // noinspection ES6MissingAwait
          this.$store.dispatch("getGlobalFeatureGroups");

          return true;
        }
      } catch (ex) {
        this.emit("error", ex);
      }

      return false;
    },
    async deleteGroup(group) {
      try {
        const confirmRes = await this.$confirm(
          `Wollen Sie die Gruppe wirklich löschen?`,
          confirmWarningConfig
        );
        if (confirmRes) {
          const headers = getTokenHeader();
          const res = await axios.delete(
            `${env.poi_api_client_url}featureGroup/${group._id}`,
            { headers }
          );
          if (res.status === 200) {
            await this.$store.dispatch("getGlobalFeatureGroups");

            this.$emit("success", "Erfolgreich gelöscht!");
            this.snackbarColor = "#00636C";
            this.snackbar = true;
          } else
            this.emit(
              "error",
              `Deletion failed with status: ${res.status}, reason: (${res.statusText})`
            );
        }
      } catch (ex) {
        this.emit("error", ex);
      }
    },
    async deleteFeature(featureId) {
      if (featureId) {
        try {
          const confirmRes = await this.$confirm(
            `Wollen Sie dieses Merkmal wirklich löschen?`,
            confirmWarningConfig
          );
          if (confirmRes) {
            const headers = getTokenHeader();
            const res = await axios.delete(
              `${env.poi_api_client_url}feature/${featureId}`,
              { headers }
            );
            if (res.status === 200) {
              // No need to wait for this dispatch here
              // noinspection ES6MissingAwait
              this.$store.dispatch("getGlobalFeatureGroups");
              this.$emit("success", "Feature wurde erfolgreich gelöscht");
            }
          }
        } catch (error) {
          console.error(error);
        }
      }
    }
  },
  computed: {
    normalizedSearchTerm() {
      return (this.searchTerm || "").trim().toLowerCase();
    },
    hasActiveSearch() {
      return this.normalizedSearchTerm.length > 0;
    },
    filteredFeatureGroups() {
      if (!this.hasActiveSearch) return this.featureGroups;
      return (this.featureGroups || []).filter(group => {
        return this.groupMatchesSearch(group) || this.hasMatchingFeature(group);
      });
    },
    editDialogTitle() {
      if (this.editDialogType === "group") return "Merkmalsgruppe bearbeiten";
      return this.featureDialogMode === "create"
        ? "Merkmal hinzufügen"
        : "Merkmal bearbeiten";
    },
    editDialogSubline() {
      if (this.editDialogType === "group")
        return "Metadaten, Zuordnungen und Sichtbarkeit der Gruppe anpassen.";
      return this.featureDialogMode === "create"
        ? "Neues Merkmal für die gewählte Gruppe anlegen."
        : "Eigenschaften, Mapping und Berechtigungen des Merkmals anpassen.";
    },
    editDialogIcon() {
      return this.editDialogType === "group"
        ? "mdi-view-list-outline"
        : "mdi-tune-variant";
    },
    cSelectedFeatureGroups: {
      get() {
        return this.selectedFeatureGroups;
      },
      set(val) {
        this.$emit("setFeatureGroups", val);
      }
    },
    categoryGroups() {
      return this.$store.state.categoryGroups;
    },
    categoryTypes() {
      return this.$store.state.categoryTypes;
    },
    selectedPrincipal() {
      return this.$store.state.selectedPrincipal;
    }
  },
  watch: {
    async selectedPrincipal() {
      await this.$store.dispatch("getCategoryGroups");
    }
  },
  components: { EditFeature }
};
